import { getProxiedServiceUrl } from '@xengage/gw-portals-url-js';
import { JsonRPCService } from '@xengage/gw-portals-transport-js';
import { Submission } from 'gw-capability-quoteandbind';
import { MLMultiLobsDataHolderService, withRetry } from 'ml-services-common-react';
import _ from 'lodash';

/**
 * Invokes the given method on the backend endpoint passing the given data as parameters
 *
 * @param {any} method the method to invoke on the backend endpoint
 * @param {any} data the data to be passed as part of the invokation on the backend
 * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
 * @returns {Promise} the promise from the backend call
 */
function processSubmission(method, data, additionalHeaders = {}) {
    return withRetry(() => JsonRPCService.send(getProxiedServiceUrl('gatewayquote/quote'), method, data, additionalHeaders)).then(
        (response) => {
            if (response && response.baseData) {
                return new Submission(response);
            }

            return response;
        }
    );
}

function startMultiLobsJobFromSubmission(submission, additionalHeaders = {}) {
    MLMultiLobsDataHolderService.startJob(
        submission.baseData.accountNumber,
        submission.quoteID,
        submission.baseData.productCode,
        submission.baseData.accountHolder.primaryAddress.postalCode,
        additionalHeaders
    );
}

/**
 * This is the server responsible for dealing with the Quote and Buy backend operations
 *
 * @export
 * @class LoadSaveService
 */
export default class LoadSaveService {
    /**
     * Updates, saves and performs a quote on an existing submission.
     * Generates quotes for all available product offerings.
     *
     * @param {Object} data tge submission that will be saved
     * @returns {Promise} the promise from the backend call
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @memberof LoadSaveService
     */
    static saveAndQuoteSubmission(data, additionalHeaders = {}) {
        const dataCopy = { ...data };

        if (dataCopy.bindData) {
            delete dataCopy.bindData;
        }

        if (dataCopy.quoteData) {
            delete dataCopy.quoteData;
        }

        if (dataCopy.additionalPaymentQuoteRequests_ML) {
            delete dataCopy.additionalPaymentQuoteRequests_ML;
        }

        const productName = _.camelCase(_.get(dataCopy, 'baseData.productName', undefined));

        if (productName !== undefined) {
            // In V9 this information was part of the quoteData, this needs to be deleted separately
            _.set(dataCopy, `lobData.${productName}.offerings`, []);
        }

        return processSubmission('saveAndQuote', [dataCopy], additionalHeaders);
    }

    /**
     * Retrieves a submission.
     *
     * @param {Object} data the retrieval payload (QuoteRetrievalDTO)
     * @returns {Promise} the promise from the backend call
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @memberof LoadSaveService
     */
    static retrieveSubmission(data, additionalHeaders = {}) {
        const postalCode = _.get(data, 'postalCode');

        if (postalCode && postalCode.length > 5) {
            data.postalCode = data.postalCode.substring(0, 5);
        }

        const isFromIAPortal = sessionStorage.getItem('fromIAPortal');
        const userSubmitted = sessionStorage.getItem('userSubmitted');
        const userSubmitAction = sessionStorage.getItem('userSubmitAction');

        sessionStorage.clear();

        if (isFromIAPortal === 'true') {
            sessionStorage.setItem('fromIAPortal', true);
        }

        if (userSubmitAction === 'true') {
            sessionStorage.setItem('userSubmitted', userSubmitted);
            sessionStorage.setItem('userSubmitAction', userSubmitAction);
        }

        sessionStorage.setItem("quoteType" ,'Retrieve');
        sessionStorage.setItem("QuoteRetrieved" , true);

        return processSubmission('retrieve', [data], additionalHeaders).then((result) => {
            sessionStorage.setItem("isCompRater", _.get(result, 'hvqDiscrepancies_ML', undefined) !== undefined);
            sessionStorage.setItem("compRaterVendorName", _.get(result, 'compRaterVendorName_ML', undefined));
            sessionStorage.setItem("isBundle", _.get(result, 'isBundle_ML', undefined));

            return result;
        });
    }

    /**
     * Updates a quoted submission with LOB Data.
     *
     * @param {Object} data the submission to be saved as draft
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static updateQuotedSubmissionWithLOBData(data, additionalHeaders = {}) {
        return processSubmission('updateQuotedSubmissionWithLOBData', [data], additionalHeaders);
    }

    /**
     * Retrieves a submission.
     *
     * @param {string} quoteID quoteID of submission that needs to be found
     * @param {string} sessionUUID current session id
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static retrievePaymentPlans(quoteID, sessionUUID, additionalHeaders = {}) {
        return processSubmission('retrievePaymentPlans', [quoteID, sessionUUID], additionalHeaders);
    }

    /**
     * Binds the submission.
     *
     * @param {Object} data the submission to be bound
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static bindSubmission(data, additionalHeaders = {}) {
        return processSubmission('bind', [data], additionalHeaders);
    }

    /**
     * Updates an existing draft submission.
     *
     * @param {Object} data the submission to be saved as draft
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static updateDraftSubmission(data, additionalHeaders = {}) {
        // use datacopy so that components with data callbacks aren't effected by deletes
        const dataCopy = { ...data };

        // CAUTION the below method remove script_ML and hvqDiscrepancies_ML in the submission
        // which will causing the discrepancy VM of the origianl submission is not accessible
        data.beforeSave();

        if (dataCopy.bindData) {
            delete dataCopy.bindData;
        }

        if (dataCopy.quoteData) {
            delete dataCopy.quoteData;
        }

        const lobPathString = _.camelCase(_.get(dataCopy, 'baseData.productName', undefined));
        if (lobPathString !== undefined && lobPathString !=='homeowners') {
            _.set(dataCopy, `lobData.${lobPathString}.offerings`, []);
        }

        return processSubmission('updateDraftSubmission', [dataCopy], additionalHeaders).then((result) => {
            startMultiLobsJobFromSubmission(result, additionalHeaders);

            return result;
        });
    }

    /**
     * Updates a quoted submission.
     *
     * @param {Object} data the submission to be saved as draft
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static updateQuotedSubmission(data, additionalHeaders = {}) {
        return processSubmission('updateQuotedSubmission', [data], additionalHeaders);
    }

    /**
     * Updates coverages.
     *
     * @param {Object} quoteID quoteID
     * @param {Object} sessionUUID sessionUUID
     * @param {Object} lobCoverages the coverages to be saved
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */

    static updateCoverages(quoteID, sessionUUID, lobCoverages, additionalHeaders = {}) {
        return processSubmission(
            'updateCoverages',
            [quoteID, sessionUUID, lobCoverages],
            additionalHeaders
        );
    }

    /**
     * Approve Underwriting Issue.
     *
     * @param {Object} quoteID quoteID
     * @param {Object} uwIssue uwIssue
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static approveUnderwritingIssue(quoteID, uwIssue, additionalHeaders = {}) {
        return processSubmission(
            'approveUnderwritingIssue',
            [quoteID, uwIssue],
            additionalHeaders
        );
    }


    /**
     * Update modifiers.
     *
     * @param {Object} data the submissionDTO
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise} the promise from the backend call
     * @memberof LoadSaveService
     */
    static updateModifiers(data, additionalHeaders = {}) {
        return processSubmission('updateModifiers', [data], additionalHeaders);
    }

    static syncQuestionSetML(quoteID, qsDto, additionalHeaders = {}) {
        return processSubmission('syncQuestionSetML', [quoteID, qsDto], additionalHeaders);
    }

    static getScript(id, scriptRequestDTOML, additionalHeaders = {}) {
        return processSubmission('getScript', [id, scriptRequestDTOML], additionalHeaders);
    }

    /**
     * Creates new submission job and generates a session to be used during the submission process.
     *
     * @param {Object} data the submission
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise}
     * @memberof LoadSaveService
     */
    static createSubmission(data, additionalHeaders = {}) {
        return processSubmission('create', [data], additionalHeaders).then((result) => {
            startMultiLobsJobFromSubmission(result, additionalHeaders);

            return result;
        });
    }

    static createQuoteProposal(quoteID, periodID, isBundled, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('gatewayquote/quote'),
            'createQuoteProposal',
            [quoteID, periodID, isBundled],
            additionalHeaders
        );
    }

    static syncLocationQuestionSet(quoteID, locationID, qsDto, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('quote'),
            'syncLocationQuestionSet',
            [quoteID, locationID, qsDto],
            additionalHeaders
        );
    }

    static getProducerEligibilityExt(quoteID, producerCode, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('gatewayquote/quote'), 
            'getProducerEligibility', 
            [quoteID, producerCode], 
            additionalHeaders
        );
    }

    static takeOverSubmission(quoteID, zipCode, additionalHeaders = {}) {
        if (zipCode && zipCode.length > 5) {
            zipCode = zipCode.substr(0, 5);
        }

        return processSubmission('takeOver', [quoteID, zipCode], additionalHeaders);
    }

    static setChoiceAgentSubmission(submissionDTO, additionalHeaders = {}) {
        return processSubmission('setChoiceAgentSubmission', [submissionDTO], additionalHeaders);
    }

    static getAllBundledQuotes(quoteID, additionalHeaders = {}) {
        return processSubmission('getAllBundledQuotes', [quoteID], additionalHeaders);
    }
    
    static preBind (submissionDTO, additionalHeaders = {}) {
        return processSubmission('preBind', [submissionDTO], additionalHeaders);
    }

    static resolveHVQDiscrepancies(data, additionalHeaders = {}) {
        return processSubmission('resolveHVQDiscrepancies', [data], additionalHeaders);
    }

    static setQuotePolicyNickname(quoteDTO, additionalHeaders = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('gatewayquote/quote'),
            'setQuotePolicyNickname', [quoteDTO], additionalHeaders);
    }

    static getPaymentInstruments(quoteID, sessionUUID, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('gatewayquote/quote'),
            'getPaymentInstruments',
            [quoteID, sessionUUID],
            additionalHeaders
        );
    }

    static HOShouldDisplayAnimalExclusionWarning(quoteID, additionalHeaders = {}) {
        return processSubmission('HOShouldDisplayAnimalExclusionWarning', [quoteID], additionalHeaders);
    }

    static isPeriodBound(data, additionalHeaders = {}) {
        return processSubmission('isPeriodBound', [data], additionalHeaders);
    }

    static getSubmissionDetails(data, additionalHeaders = {}) {
        return processSubmission('getSubmissionDetails', [data], additionalHeaders);
    }

    static getAdditionalInterestCandidatesML(quoteID, additionalHeaders = {}) {
        return processSubmission('getAdditionalInterestCandidates', [quoteID], additionalHeaders);
    }

    static getBoundSubmissionDetails(data, additionalHeaders = {}) {
        return processSubmission('getBoundSubmissionDetails', [data], additionalHeaders);
    }


    static manualOrderMvr(data, additionalHeaders = {}) {
        return processSubmission('manualOrderMvr', [data], additionalHeaders);
    }

    static assignPolicyNumbersToModifiers(data, additionalHeaders = {}) {
        return processSubmission('assignPolicyNumbersToModifiers_Ext', [data.quoteID, data.sessionUUID, data.portalCurrentStep_ML], additionalHeaders);
        // return processSubmission('assignPolicyNumbersToModifiers', [data], additionalHeaders);
    }
    
    static selectOffering(data, additionalHeaders = {}) {
        return processSubmission('selectOffering', [data], additionalHeaders);
    }

    /**
     * Update Producer Code.
     *
     * @param {Number} submission the submission
     * @param {Object} [additionalHeaders] additional headers to pass to the backend (e.g. auth)
     * @returns {Promise}
     * @memberof LoadSaveService
     */
    static updateProducerCode(jobId,producerCode_ML, additionalHeaders = {}) {
        return JsonRPCService.send(
            getProxiedServiceUrl('gatewayquote/quote'),
            'updateProducerCode_Ext',
            [jobId,producerCode_ML],
            additionalHeaders
        );
    }
    static refreshPreBindContingencies (jobId, additionalHeaders = {}) {
        return processSubmission('refreshPreBindContingencies', [jobId], additionalHeaders);
    }

    static refreshOpenOrClosedPreBindContingencies(jobId, additionalHeaders = {}) {
        return processSubmission('refreshOpenOrClosedPreBindContingencies', [jobId], additionalHeaders);
    }

    static getIndicativeQuote_Ext(quoteID, sessionUUID, crossSellData, additionalHeaders = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('gatewayquote/quote'),
            'getIndicativeQuote_Ext', [quoteID, sessionUUID, crossSellData], additionalHeaders);
    }

    static getSpecialtyVehicleMake(quoteId, productType, year, additionalHeaders = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('gatewayquote/quote'),
        'getSpecialtyVehicleMake', [quoteId, productType, year], additionalHeaders);
    }

    static getSpecialtyVehicleModel(quoteId, productType, year, make, additionalHeaders = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('gatewayquote/quote'),
        'getSpecialtyVehicleModel', [quoteId, productType, year, make], additionalHeaders);
    }

    static createSpecialtyVehicleModel(quoteId, specialtyVehicleList, additionalHeaders = {}) {
        return JsonRPCService.send(getProxiedServiceUrl('gatewayquote/quote'),
        'createSpecialtyVehicleModel', [quoteId, specialtyVehicleList], additionalHeaders);
    }

    static sendHomeInspectionOrder(quoteID, additionalHeaders = {}) {
        return processSubmission('sendHomeInspectionOrder_Ext', [quoteID], additionalHeaders);
    }

    static isProducerCodeApplicableForQuoteAssist_Ext (producerCode, additionalHeaders = {}) {
        return processSubmission('isProducerCodeApplicableForQuoteAssist_Ext', [producerCode], additionalHeaders);
    }

    static callVeriskIntegration (jobId, additionalHeaders = {}) {
        return processSubmission('callVeriskIntegration', [jobId], additionalHeaders);
    }

    static updateDwellingField (jobId, selectedValue, additionalHeaders={}) {
        return processSubmission('updateDwellingField',[jobId, selectedValue], additionalHeaders);
    }
}